import React, { useRef } from 'react';
import { X, Download, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const CertificateFOAD = ({ data, onClose }) => {
  const certRef = useRef(null);
  if (!data) return null;

  const {
    prenom = '', nom = '', courseTitre = '', courseDescription = '',
    dureeEstimee = '', noteSur10 = 0, dateStr = '', refNumber = '',
    objectifs = '', nbModules = 0, tamponImg = '', gerantNom = ''
  } = data;

  const handlePrint = () => {
    const content = certRef.current;
    if (!content) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Certificat FOAD - ${prenom} ${nom}</title>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Open+Sans:wght@400;600&display=swap" rel="stylesheet">
      <style>
        @media print { body { margin: 0; } .cert-wrapper { box-shadow: none !important; } }
        body { margin: 0; padding: 20px; background: #e5e7eb; font-family: 'Open Sans', sans-serif; }
        .cert-wrapper { width: 1050px; min-height: 740px; margin: 0 auto; background: #fff; position: relative; overflow: hidden; box-shadow: 0 4px 30px rgba(0,0,0,.15); }
        .cert-bg { position: absolute; inset: 0; background: linear-gradient(135deg, #f8fafb 0%, #eef2f5 100%); }
        .cert-border-top { height: 8px; background: linear-gradient(90deg, #2c5f6e 0%, #3d8a9c 50%, #2c5f6e 100%); }
        .cert-header { display: flex; justify-content: space-between; padding: 12px 40px; background: #2c5f6e; color: #c8dce2; font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase; }
        .cert-body { position: relative; z-index: 1; padding: 36px 50px 30px; }
        .cert-title { font-family: 'Montserrat', sans-serif; font-size: 38px; font-weight: 800; color: #1a3a44; line-height: 1.15; margin: 0 0 28px; letter-spacing: -0.5px; }
        .cert-subtitle { font-size: 15px; color: #5a7a84; margin: 0 0 10px; }
        .cert-name-wrapper { display: flex; align-items: center; margin: 16px 0 24px; }
        .cert-name-bar { width: 5px; height: 52px; background: #2c5f6e; border-radius: 3px; margin-right: 18px; flex-shrink: 0; }
        .cert-name { font-family: 'Montserrat', sans-serif; font-size: 32px; font-weight: 700; color: #1a3a44; margin: 0; letter-spacing: -0.3px; }
        .cert-desc { font-size: 14px; color: #4a6670; line-height: 1.7; margin: 0 0 32px; max-width: 580px; }
        .cert-bottom { display: flex; gap: 50px; align-items: flex-end; }
        .cert-metrics { display: flex; flex-direction: column; gap: 10px; }
        .cert-metric { background: #eef4f6; padding: 10px 18px; border-left: 4px solid #2c5f6e; border-radius: 0 6px 6px 0; font-size: 14px; color: #1a3a44; }
        .cert-metric span { font-weight: 700; }
        .cert-signatures { display: flex; gap: 40px; }
        .cert-sig { text-align: center; }
        .cert-sig-line { width: 120px; height: 1px; background: #b0c4cc; margin: 0 auto 8px; }
        .cert-sig-name { font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 13px; color: #1a3a44; }
        .cert-sig-role { font-size: 11px; color: #6a8a94; margin-top: 2px; }
        .cert-logo { text-align: center; }
        .cert-logo img { height: 55px; border-radius: 6px; }
        .cert-stamp img { height: 80px; object-fit: contain; margin-bottom: 8px; }
        .cert-badge { position: absolute; top: 30px; right: 40px; width: 120px; height: 120px; }
        .badge-outer { width: 120px; height: 120px; border-radius: 50%; background: linear-gradient(145deg, #e8b830 0%, #c9952a 40%, #daa520 60%, #f0d060 100%); display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 15px rgba(180,140,20,.35); }
        .badge-inner { width: 98px; height: 98px; border-radius: 50%; border: 2px solid rgba(255,255,255,.4); display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
        .badge-stars { font-size: 14px; color: #1a3a44; letter-spacing: 3px; margin-bottom: 2px; }
        .badge-text { font-family: 'Montserrat', sans-serif; font-weight: 800; font-size: 13px; color: #1a3a44; letter-spacing: 1.5px; }
        .badge-year { font-family: 'Montserrat', sans-serif; font-weight: 600; font-size: 18px; color: #1a3a44; margin-top: 1px; }
        .cert-footer { height: 6px; background: linear-gradient(90deg, #2c5f6e 0%, #3d8a9c 50%, #2c5f6e 100%); }
      </style>
    </head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="relative max-w-[1100px] w-full" onClick={e => e.stopPropagation()}>
        {/* Action buttons */}
        <div className="flex justify-end gap-2 mb-3">
          <Button onClick={handlePrint} className="bg-[#2c5f6e] hover:bg-[#1a3a44] text-white" data-testid="cert-download">
            <Download className="h-4 w-4 mr-2" /> Telecharger PDF
          </Button>
          <Button variant="outline" onClick={onClose} className="bg-white">
            <X className="h-4 w-4 mr-1" /> Fermer
          </Button>
        </div>

        {/* Certificate */}
        <div ref={certRef}>
          <div className="cert-wrapper" style={{ width: '1050px', minHeight: '740px', margin: '0 auto', background: '#fff', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 30px rgba(0,0,0,.15)', fontFamily: "'Open Sans', sans-serif" }}>
            {/* Background */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #f8fafb 0%, #eef2f5 100%)' }} />

            {/* Top border */}
            <div style={{ height: '8px', background: 'linear-gradient(90deg, #2c5f6e 0%, #3d8a9c 50%, #2c5f6e 100%)' }} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 40px', background: '#2c5f6e', color: '#c8dce2', fontSize: '12px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              <span>Reference : FISL-{refNumber}-{new Date().getFullYear()}</span>
              <span>Certifie le : {dateStr}</span>
            </div>

            {/* Body */}
            <div style={{ position: 'relative', zIndex: 1, padding: '36px 50px 30px' }}>
              {/* Title */}
              <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '38px', fontWeight: 800, color: '#1a3a44', lineHeight: 1.15, margin: '0 0 28px', letterSpacing: '-0.5px' }}>
                CERTIFICAT DE COURS<br />EN LIGNE
              </h1>

              {/* Badge */}
              <div style={{ position: 'absolute', top: '30px', right: '40px', width: '120px', height: '120px' }}>
                <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'linear-gradient(145deg, #e8b830 0%, #c9952a 40%, #daa520 60%, #f0d060 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(180,140,20,.35)' }}>
                  <div style={{ width: '98px', height: '98px', borderRadius: '50%', border: '2px solid rgba(255,255,255,.4)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#1a3a44', letterSpacing: '3px', marginBottom: '2px' }}>&#9733;&#9733;&#9733;</div>
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '13px', color: '#1a3a44', letterSpacing: '1.5px' }}>E-LEARNING</div>
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: '18px', color: '#1a3a44', marginTop: '1px' }}>{new Date().getFullYear()}</div>
                  </div>
                </div>
              </div>

              {/* Certification text */}
              <p style={{ fontSize: '15px', color: '#5a7a84', margin: '0 0 10px' }}>Nous certifions que</p>

              {/* Name */}
              <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 24px' }}>
                <div style={{ width: '5px', height: '52px', background: '#2c5f6e', borderRadius: '3px', marginRight: '18px', flexShrink: 0 }} />
                <h2 style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '32px', fontWeight: 700, color: '#1a3a44', margin: 0, letterSpacing: '-0.3px' }}>
                  {prenom.toUpperCase()} {nom.toUpperCase()}
                </h2>
              </div>

              {/* Description */}
              <p style={{ fontSize: '14px', color: '#4a6670', lineHeight: 1.7, margin: '0 0 32px', maxWidth: '580px' }}>
                A achev&eacute; le parcours de cours "<strong>{courseTitre}</strong>"
                {nbModules > 0 && <> comprenant {nbModules} modules interactifs</>}.
                {objectifs && <> {objectifs}.</>}
              </p>

              {/* Bottom section */}
              <div style={{ display: 'flex', gap: '50px', alignItems: 'flex-end' }}>
                {/* Metrics */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ background: '#eef4f6', padding: '10px 18px', borderLeft: '4px solid #2c5f6e', borderRadius: '0 6px 6px 0', fontSize: '14px', color: '#1a3a44' }}>
                    Heures completees : <strong>{dureeEstimee || 'N/A'}</strong>
                  </div>
                  <div style={{ background: '#eef4f6', padding: '10px 18px', borderLeft: '4px solid #2c5f6e', borderRadius: '0 6px 6px 0', fontSize: '14px', color: '#1a3a44' }}>
                    Modules completes : <strong>{nbModules}</strong>
                  </div>
                  <div style={{ background: '#eef4f6', padding: '10px 18px', borderLeft: '4px solid #2c5f6e', borderRadius: '0 6px 6px 0', fontSize: '14px', color: '#1a3a44' }}>
                    Note finale : <strong>{noteSur10}/10</strong>
                  </div>
                </div>

                {/* Logo + Signatures */}
                <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-end' }}>
                  {/* Logo */}
                  <div style={{ textAlign: 'center' }}>
                    <img src="/logo-fisl.png" alt="FISL" style={{ height: '55px', borderRadius: '6px' }} crossOrigin="anonymous" />
                  </div>

                  {/* Tampon du centre + Directeur */}
                  <div style={{ textAlign: 'center' }}>
                    {tamponImg ? (
                      <img src={tamponImg} alt="Tampon centre" style={{ height: '80px', objectFit: 'contain', marginBottom: '8px' }} crossOrigin="anonymous" />
                    ) : (
                      <div style={{ border: '2px solid #2c5f6e', borderRadius: '8px', padding: '8px 14px', marginBottom: '8px' }}>
                        <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '11px', color: '#2c5f6e', letterSpacing: '1px', textTransform: 'uppercase' }}>FISL</div>
                        <div style={{ fontSize: '9px', color: '#5a7a84', marginTop: '1px' }}>Formation Motonautisme</div>
                      </div>
                    )}
                    <div style={{ width: '120px', height: '1px', background: '#b0c4cc', margin: '0 auto 8px' }} />
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: '13px', color: '#1a3a44' }}>{gerantNom || 'Directeur'}</div>
                    <div style={{ fontSize: '11px', color: '#6a8a94', marginTop: '2px' }}>Centre de Formation</div>
                  </div>

                  {/* FISL signature */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '120px', height: '1px', background: '#b0c4cc', margin: '0 auto 8px' }} />
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: '13px', color: '#1a3a44' }}>FISL</div>
                    <div style={{ fontSize: '11px', color: '#6a8a94', marginTop: '2px' }}>Formation Motonautisme</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer border */}
            <div style={{ height: '6px', background: 'linear-gradient(90deg, #2c5f6e 0%, #3d8a9c 50%, #2c5f6e 100%)', position: 'absolute', bottom: 0, left: 0, right: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateFOAD;
